import React, { useRef, useState, forwardRef, useImperativeHandle, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, IRootState } from '../../../../redux/rootReducer';
import Loader from '../../../common/loader/Loader';
import CustModal from '../../../common/custmodal/CustModal';
import MageDotsIcon from '../../../svgIcons/MageDotsIcon';
import AddVendor from '../../addVendors/AddVendor';
import { deleteVendor, getVendor } from '../../../../redux/vendor/actions/vendors.action';
import { hasPermission } from '../../../auth/login/Login';
import { parsePhoneNumber } from "awesome-phonenumber";
import styles from '../VendorAppointments.module.css'

interface VendorMapped {
    id?: string;
    name: string;
    propertyName?: string;
    email: string;
    phone: string;
    accessCode: string;
}

interface VendorsTableProps {
    searchTerm: string;
    pageNumber: number;
    pageLimit: number;
    totalCount: number;
    setTotalCount: React.Dispatch<React.SetStateAction<number>>;
    onPageChange: (page: number) => void;
    setShowToast: React.Dispatch<React.SetStateAction<boolean>>;
    setToastMessage: React.Dispatch<React.SetStateAction<string>>;
    setToastType: React.Dispatch<React.SetStateAction<string>>;
}

const VendorsTable = forwardRef<{ refetchData: () => void }, VendorsTableProps>(({
    searchTerm,
    pageNumber,
    pageLimit,
    totalCount,
    setTotalCount,
    onPageChange,
    setShowToast,
    setToastMessage,
    setToastType
}, ref) => {
    const dispatch: AppDispatch = useDispatch();
    const [showActionBox, setShowActionBox] = useState<string | null>(null);
    const [actionBoxPosition, setActionBoxPosition] = useState<'top' | 'bottom'>('bottom');
    const actionButtonRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
    const [updateVendor, setUpdateVendor] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState<typeof formattedVendorList[0] | null>(null);
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [isAPILoading, setIsAPILoading] = useState(false);

    const canDeleteVendor = hasPermission('Self-Guided Tours', 'Vendor', 'delete');
    const canUpdateVendor = hasPermission('Self-Guided Tours', 'Vendor', 'update');

    const { data: vendorListData, loading: loadingVendorList } = useSelector((state: IRootState) => state?.vendorListReducer);

    const getVendorListData = () => {
        dispatch(getVendor({ search: searchTerm, page: pageNumber, limit: pageLimit, setTotalCount }));
    }

    // Expose refetch method to parent component
    useImperativeHandle(ref, () => ({
        refetchData: getVendorListData
    }));

    // Auto-refetch when search term or page number changes
    useEffect(() => {
        getVendorListData();
    }, [searchTerm, pageNumber]);

    const formatPhoneNumber = (phone: string) => {
        if (!phone || phone === "-") return "-";

        try {
            const phoneNumber = parsePhoneNumber(`+${phone}`);
            if (phoneNumber && phoneNumber.valid) {
                return phoneNumber.number.international;
            }
            return phone; // Return original if parsing fails
        } catch (error) {
            console.warn('Phone number parsing failed:', error);
            return phone; // Return original if parsing fails
        }
    };

    const formattedVendorList = vendorListData
        .map((vendor: VendorMapped) => ({
            propertyName: vendor.propertyName,
            name: vendor.name,
            email: vendor.email ? vendor.email : "-",
            phone: formatPhoneNumber(vendor.phone),
            accessCode: vendor.accessCode ? vendor.accessCode : "- ",
            id: vendor.id
        }));

    const handleActionButtonClick = (index: string) => {
        const rowRef = actionButtonRefs.current[index];

        if (rowRef) {
            const rect = rowRef.getBoundingClientRect();
            const parentRect = rowRef.closest(`.${CSS.escape(styles.vendorAppointmentsTableListing)}`)?.getBoundingClientRect();

            if (parentRect) {
                const parentBottom = parentRect.bottom;

                if (rect.bottom > parentBottom - 100) {
                    setActionBoxPosition('top');
                } else {
                    setActionBoxPosition('bottom');
                }

                setShowActionBox(showActionBox !== index ? index : '');
            }
        }
    };

    const onDeleteVendorSuccess = (message: string) => {
        setIsAPILoading(false);
        setToastType('success');
        setShowToast(true);
        setToastMessage(message);
        getVendorListData();
    }

    const onDeleteVendorFailure = (message: string) => {
        setIsAPILoading(false);
        setToastType('error');
        setShowToast(true);
        setToastMessage(message)
    }

    const onDeleteVendorData = () => {
        if (selectedVendor?.id) {
            setIsAPILoading(true);
            setShowConfirmModal(false);
            dispatch(deleteVendor(selectedVendor.id, onDeleteVendorSuccess, onDeleteVendorFailure))
        }
    }

    const ActionModalBody = () => {
        return (
            <div className={styles.vendorAppointmentsActionModal}>
                <h2 className={styles.vendorAppointmentsTitle}>{`Are you sure you want to delete?`}</h2>
                <p className={styles.vendorAppointmentsDescription}>
                    Vendor cannot be recovered once deleted.
                </p>
                <div className={styles.vendorAppointmentsActions}>
                    <button className={styles.vendorAppointmentsCancelButton} onClick={() => setShowConfirmModal(false)}>
                        Cancel
                    </button>
                    <button className={styles.vendorAppointmentsConfirmButton}
                        onClick={onDeleteVendorData}>
                        Delete
                    </button>
                </div>
            </div >
        )
    }

    return (
        <>
            {(loadingVendorList || isAPILoading) && <Loader />}

            <CustModal
                open={updateVendor}
                setOpen={setUpdateVendor}
                bodyData={
                    selectedVendor && (
                        <AddVendor
                            setOpen={setUpdateVendor}
                            setShowToast={setShowToast}
                            setToastMessage={setToastMessage}
                            setToastType={setToastType}
                            vendorListData={selectedVendor}
                            getVendorListData={getVendorListData}
                        />
                    )
                }
            />

            <CustModal
                open={showConfirmModal}
                bodyData={<ActionModalBody />}
                onCloseModal={() => {
                    setShowConfirmModal(false);
                }}
            />

            <div className={styles.vendorAppointmentsTableListing}>
                <table>
                    <thead>
                        <tr>
                            <th>Vendor Name</th>
                            <th>Property Name</th>
                            <th>Email</th>
                            <th>Contact No</th>
                            <th>Access code</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {formattedVendorList?.length > 0
                            ? formattedVendorList.map((item: VendorMapped, index: number) => (
                                <tr key={index} ref={(el) => (actionButtonRefs.current[index.toString()] = el)}>
                                    <td>{item.name} </td>
                                    <td>{item.propertyName}</td>
                                    <td>{item.email}</td>
                                    <td>{item.phone}</td>
                                    <td>{item.accessCode}</td>
                                    <td>
                                        {!!showActionBox && (
                                            <div
                                                className={styles.vendorAppointmentsDropDownBackDrop}
                                                onClick={() => setShowActionBox('')}></div>
                                        )}
                                        <div className={styles.vendorAppointmentsActionButtonWrapper}>
                                            <button
                                                disabled={!(canUpdateVendor || canDeleteVendor)}
                                                className={styles.vendorAppointmentsMageDotsIcon}
                                                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                                    event.stopPropagation();
                                                    handleActionButtonClick(index?.toString());
                                                }}>
                                                <MageDotsIcon />
                                            </button>
                                            <div
                                                className={`${styles.vendorAppointmentsActionContainer} ${index?.toString() === showActionBox ? styles.active : ''
                                                    } ${actionBoxPosition === 'top' ? styles.top : ''}`}>
                                                {canUpdateVendor && <div
                                                    className={styles.vendorAppointmentsActionBox}
                                                    role="button"
                                                    onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                        event.stopPropagation();
                                                        setShowActionBox(null);
                                                        setSelectedVendor(vendorListData[index]);
                                                        setUpdateVendor(true);
                                                    }}>
                                                    <span>{'Edit'}</span>
                                                </div>}
                                                {canDeleteVendor && <div
                                                    className={styles.vendorAppointmentsActionBox}
                                                    role="button"
                                                    onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                        event.stopPropagation();
                                                        setShowActionBox(null);
                                                        setSelectedVendor(vendorListData[index]);
                                                        setShowConfirmModal(true);
                                                    }}>
                                                    <span>{'Delete'}</span>
                                                </div>}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))
                            : !loadingVendorList && (
                                <tr>
                                    <td className={styles.noData} colSpan={6}>
                                        No Vendor Found
                                    </td>
                                </tr>
                            )}
                    </tbody>
                </table>
            </div>
        </>
    );
});

VendorsTable.displayName = 'VendorsTable';

export default VendorsTable;
